import { useState, useEffect, useRef, useMemo } from 'react';
import { useUnreadMessagesContext } from '../contexts/UnreadMessagesContext';

let hookInstanceCounter = 0;

const useUnreadMessages = (tripIds = []) => {
  const [instanceId] = useState(() => `hook-${++hookInstanceCounter}-${Date.now()}`);
  
  // Use centralized context instead of individual hook logic
  const {
    unreadCounts: allUnreadCounts,
    loading,
    subscribeToTrips,
    unsubscribeFromTrips,
    markAsRead,
    refresh,
    connected
  } = useUnreadMessagesContext();

  const previousTripIdsRef = useRef([]);
  
  // Memoize tripIds to prevent unnecessary re-subscriptions
  const stableTripIds = useMemo(() => {
    const sortedIds = [...tripIds].sort((a, b) => a - b);
    const idsString = sortedIds.join(',');
    const prevIdsString = previousTripIdsRef.current.join(',');
    
    if (idsString !== prevIdsString) {
      console.log(`🔄 [${instanceId}] Trip IDs changed:`, { 
        old: previousTripIdsRef.current, 
        new: sortedIds
      });
      previousTripIdsRef.current = sortedIds;
      return sortedIds;
    }
    
    return previousTripIdsRef.current;
  }, [tripIds, instanceId]);

  // Subscribe to trips when tripIds change
  useEffect(() => {
    if (stableTripIds.length === 0) {
      return;
    }

    console.log(`📊 [${instanceId}] Subscribing to trips:`, stableTripIds);
    subscribeToTrips(stableTripIds, instanceId);

    // Cleanup: unsubscribe when tripIds change or component unmounts
    return () => {
      console.log(`📊 [${instanceId}] Unsubscribing from trips:`, stableTripIds);
      unsubscribeFromTrips(stableTripIds, instanceId);
    };
  }, [stableTripIds, instanceId, subscribeToTrips, unsubscribeFromTrips]);

  // Filter unread counts to only include requested trip IDs
  const filteredUnreadCounts = useMemo(() => {
    const filtered = {};
    stableTripIds.forEach(tripId => {
      filtered[tripId] = allUnreadCounts[tripId] || 0;
    });
    return filtered;
  }, [allUnreadCounts, stableTripIds]);

  return {
    unreadCounts: filteredUnreadCounts,
    loading,
    markAsRead,
    refresh,
    forceRefresh: refresh,
    connected
  };
};

export default useUnreadMessages; 